import { axialHexDistance } from '../../../hexmap/coords';
import { getHexTileMapKey } from '../../../hexmap/model/keys';
import { getTile, hexesWithinRadius, isPassable } from '../../../hexmap/model/map';
import type { HexTileMapKey, HexTileType } from '../../../hexmap/model/types';
import { gridOf, pathTo, type Reachable, tileAt } from '../../../hexmap/pathfinding';
import { buildingRange } from '../../bonuses';
import { buildingCells } from '../../footprint';
import { buildingLevel, levelStat } from '../../levels';
import { addHeading, forget, stopKind } from '../../occupancy';
import { redirect } from '../../redirect';
import { mintOrder } from '../../territory';
import type { Cell, EntityId, PlayerId, SimContext, SimState, Unit } from '../../types';
import {
  homeCity,
  isMoving,
  type OwnerCache,
  ownerCache,
  ownTerritory,
  RETRY_TICKS,
  retryAt,
  type TickCache,
} from './cache';
import { SAFE_RADIUS, THREAT_RADIUS, threatsOf } from './threat';

/** Explore automation for scouts (see index.ts and `stepScout`). */

/**
 * Scout frontier score = gain × spread / (walking cost + DISTANCE_OFFSET
 * vision radii). Gain counts the unexplored tiles within vision of the
 * frontier, those within vision of another scout's target at COVERED_WEIGHT.
 * Spread runs from SPREAD_FLOOR up to 1 with the distance to the nearest
 * other scout's target (or other scout nearer to the tile), capped at
 * SEPARATION_CAP vision radii. Highest score wins; only the
 * MAX_CANDIDATES cheapest frontier tiles are scored.
 */
const SCOUT_DISTANCE_OFFSET = 2;
const SCOUT_COVERED_WEIGHT = 0.25;
const SCOUT_SPREAD_FLOOR = 0.2;
const SCOUT_SEPARATION_CAP = 3;
const SCOUT_MAX_CANDIDATES = 300;
/** A scout keeps at least this far from a visible enemy fighter (more for ranged ones)… */
const SCOUT_DANGER_RADIUS = THREAT_RADIUS;
/** …and a step into that danger costs this much extra, one into seen rival territory this much. */
const SCOUT_DANGER_COST = 20;
const SCOUT_RIVAL_COST = 4;
/** A fleeing scout runs to its shelter, or this far from every enemy fighter it sees… */
const SCOUT_FLEE_DISTANCE = 5;
/** …preferring shelter unless another safe tile is this much cheaper to reach. */
const SCOUT_SHELTER_BIAS = 6;
/** A scout with nothing left to explore waits at home, looking again this often (ticks: 5 s). */
const SCOUT_IDLE_RETRY_TICKS = 50;
/** A scout waiting near a dangerous frontier looks again this often (ticks: 5 s). */
const SCOUT_WAIT_RETRY_TICKS = 50;
/**
 * Frontiers within this many hexes of one a scout found dangerous
 * (`auto.avoid`) that its owner doesn't see right now count as dangerous.
 */
const SCOUT_AVOID_RADIUS = 6;
/** Most dangerous frontiers a scout remembers. */
const SCOUT_MAX_AVOID = 8;
/** With the map explored, a scout picks a new patrol spot this often (ticks: 60 s). */
const SCOUT_PATROL_TICKS = 600;
/**
 * A patrol spot within this many vision radii of another own scout's target
 * is taken only when no spot is clear of them, so scouts patrol apart.
 */
const SCOUT_PATROL_SPREAD = 2;
/**
 * Most patrol spots a player can remember looking at (`PlayerState.looked`):
 * a radius-40 map's 4921 tiles at the closest spacing, for the snapshot
 * validator.
 */
const MAX_LOOKED = 5000;
/**
 * A scout whose frontier is only reachable through danger waits near it this
 * long (ticks: 30 s), then dares the way (`auto.dare`).
 */
const SCOUT_DARE_TICKS = 300;

// ── Scouts ──────────────────────────────────────────────────────────────────

/**
 * An explored tile next to an unexplored map tile. Memoised for the tick
 * (exploring happens later), as scouts ask about the same tiles many times.
 */
function isFrontier(ctx: SimContext, oc: OwnerCache, cell: Cell): boolean {
  const cellKey = getHexTileMapKey(cell);
  oc.frontier ??= new Map();
  const known = oc.frontier.get(cellKey);
  if (known !== undefined) return known;
  let frontier = false;
  if (oc.explored(cellKey)) {
    const grid = gridOf(ctx.terrain);
    const bits = unexploredBits(ctx, oc);
    const at = grid.index.get(cellKey) ?? -1;
    for (let k = 0; k < 6 && at >= 0; k++) {
      const nb = grid.neighbors[at * 6 + k];
      if (nb >= 0 && bits[nb]) {
        frontier = true;
        break;
      }
    }
  }
  oc.frontier.set(cellKey, frontier);
  return frontier;
}

/** The owner's unexplored map tiles, by grid index (computed once per tick: exploring happens later). */
function unexploredBits(ctx: SimContext, oc: OwnerCache): Uint8Array {
  if (!oc.unexploredBits) {
    const { keys } = gridOf(ctx.terrain);
    const bits = new Uint8Array(keys.length);
    for (let i = 0; i < keys.length; i++) if (!oc.explored(keys[i])) bits[i] = 1;
    oc.unexploredBits = bits;
  }
  return oc.unexploredBits;
}

/** Own exploring scouts' targets (fleeing ones have none worth avoiding). */
function scoutTargets(
  state: SimState,
  ctx: SimContext,
  owner: PlayerId,
  oc: OwnerCache
): Map<EntityId, Cell> {
  if (!oc.scoutTargets) {
    oc.scoutTargets = new Map();
    for (const u of Object.values(state.units)) {
      if (u.owner !== owner || !u.auto?.target || u.auto.phase === 'flee') continue;
      if (ctx.rules.units[u.type]?.automate !== 'explore') continue;
      oc.scoutTargets.set(u.id, u.auto.target);
    }
  }
  return oc.scoutTargets;
}

/** Own automated scouts' positions. */
function scoutPositions(
  state: SimState,
  ctx: SimContext,
  owner: PlayerId,
  oc: OwnerCache
): Map<EntityId, Cell> {
  if (!oc.scoutPositions) {
    oc.scoutPositions = new Map();
    for (const u of Object.values(state.units)) {
      if (u.owner !== owner || !u.auto) continue;
      if (ctx.rules.units[u.type]?.automate !== 'explore') continue;
      oc.scoutPositions.set(u.id, u.pos);
    }
  }
  return oc.scoutPositions;
}

/**
 * Tiles `owner`'s scouts keep out of, judged only from what the owner sees
 * right now (no fog leak): within reach of a visible enemy fighter (its range
 * plus one, at least SCOUT_DANGER_RADIUS) and within range of a visible
 * completed rival building that fires.
 */
function scoutDanger(
  state: SimState,
  ctx: SimContext,
  owner: PlayerId,
  oc: OwnerCache
): Set<HexTileMapKey> {
  if (!oc.danger) {
    const zone = new Set<HexTileMapKey>();
    const sources: Array<{ pos: Cell; radius: number }> = [];
    for (const u of Object.values(state.units)) {
      // Units inside a building are hidden and don't attack.
      if (u.owner === owner || u.inside) continue;
      const def = ctx.rules.units[u.type];
      if (!def?.actions.includes('attack') || def.strength <= 0) continue;
      if (def.targets === 'buildings') continue;
      sources.push({ pos: u.pos, radius: Math.max(SCOUT_DANGER_RADIUS, def.range + 1) });
    }
    for (const b of Object.values(state.buildings)) {
      if (b.owner === owner || b.buildProgress < 1) continue;
      const def = ctx.rules.buildings[b.type];
      if (!def || (levelStat(def, buildingLevel(b), 'strength') ?? 0) <= 0) continue;
      sources.push({ pos: b.pos, radius: buildingRange(ctx.rules, b) });
    }
    // Skip the vision pass when there's nothing to see.
    const vision = sources.length > 0 ? oc.derived.vision(owner) : null;
    for (const s of sources) {
      if (!vision?.has(getHexTileMapKey(s.pos))) continue;
      for (const c of hexesWithinRadius(s.pos, s.radius)) zone.add(getHexTileMapKey(c));
    }
    oc.danger = zone;
  }
  return oc.danger;
}

/**
 * Tiles of rival territory `owner` sees right now (the real holder of a tile
 * in sight, as the map's borders show it; nothing out of sight).
 */
function seenRivalTerritory(
  state: SimState,
  cache: TickCache,
  owner: PlayerId,
  oc: OwnerCache
): Set<HexTileMapKey> {
  if (!oc.rivalTerritory) {
    const rival = new Set<HexTileMapKey>();
    let vision: ReadonlySet<HexTileMapKey> | null = null;
    for (const [key, cityId] of cache.derived.owners()) {
      const city = state.buildings[cityId];
      if (!city || city.owner === owner) continue;
      vision ??= oc.derived.vision(owner);
      if (vision.has(key)) rival.add(key);
    }
    oc.rivalTerritory = rival;
  }
  return oc.rivalTerritory;
}

/**
 * Cheapest walks from `start` (Dial's algorithm: integer step costs), in the
 * `Reachable` shape with `dist` holding the cost, so `pathTo` follows the
 * cheapest path. Tiles come out in cost order, ties in canonical neighbour
 * order, so it's deterministic. `blocked` tiles are never entered. `stop`
 * ends the search once it returns true for a tile just reached (in `order`):
 * a caller that only reads the first tiles in cost order gets the same
 * answer, without searching the rest of a big map.
 */
function cheapestFrom(
  tiles: SimContext['terrain'],
  start: Cell,
  terrain: readonly HexTileType[],
  blocked: ReadonlySet<HexTileMapKey>,
  stepCost: (key: HexTileMapKey) => number,
  stop?: (cell: Cell, key: HexTileMapKey) => boolean
): Reachable {
  const startKey = getHexTileMapKey(start);
  const grid = gridOf(tiles);
  const startIndex = grid.index.get(startKey);
  if (startIndex === undefined || !isPassable(getTile(tiles, start), terrain)) {
    return { order: [], dist: new Map(), parent: new Map() };
  }
  // On the map's numbered grid (see pathfinding.ts `gridOf`): costs, parents,
  // and terrain (0 unknown, 1 passable, 2 not) by tile index.
  const { keys, index, neighbors } = grid;
  const n = keys.length;
  const cost = new Int32Array(n).fill(-1);
  const parents = new Int32Array(n).fill(-1);
  const passable = new Uint8Array(n);
  const done = new Uint8Array(n);
  const cells: Array<Cell | undefined> = new Array(n);
  const order: Cell[] = [];
  cost[startIndex] = 0;
  const buckets: number[][] = [[startIndex]];
  const found = (): Reachable => {
    const reached = (key: HexTileMapKey): number => {
      const i = index.get(key);
      return i === undefined ? -1 : cost[i];
    };
    return {
      order,
      dist: {
        get: (key) => {
          const d = reached(key);
          return d < 0 ? undefined : d;
        },
        has: (key) => reached(key) >= 0,
      },
      parent: {
        get: (key) => {
          const i = index.get(key);
          if (i === undefined || cost[i] < 0) return undefined;
          const p = parents[i];
          return p < 0 ? null : cells[p];
        },
        has: (key) => reached(key) >= 0,
      },
    };
  };
  for (let d = 0; d < buckets.length; d++) {
    // Step costs are at least 1, so nothing is added to the bucket in hand.
    for (const at of buckets[d] ?? []) {
      if (done[at] || cost[at] !== d) continue;
      done[at] = 1;
      const key = keys[at];
      const tile = at === startIndex ? undefined : tiles.get(key);
      const cell = tile ? { q: tile.coordinates.q, r: tile.coordinates.r } : start;
      cells[at] = cell;
      order.push(cell);
      if (stop?.(cell, key)) return found();
      for (let k = 0; k < 6; k++) {
        const nb = neighbors[at * 6 + k];
        if (nb < 0 || done[nb]) continue;
        const nk = keys[nb];
        if (blocked.has(nk)) continue;
        if (passable[nb] === 0) passable[nb] = isPassable(tiles.get(nk), terrain) ? 1 : 2;
        if (passable[nb] === 2) continue;
        const g = d + stepCost(nk);
        if (cost[nb] >= 0 && g >= cost[nb]) continue;
        cost[nb] = g;
        parents[nb] = at;
        const bucket = buckets[g];
        if (bucket) bucket.push(nb);
        else buckets[g] = [nb];
      }
    }
  }
  return found();
}

/**
 * Where a scout can walk and what it costs: around the blockers its owner
 * sees, with steps into danger (`scoutDanger`) or seen rival territory
 * costing extra, so paths go round them when there's a way. With
 * `avoidDanger` (exploring) danger tiles are blocked outright, so a chosen
 * path never crosses danger (and so is never dropped for it next tick).
 */
function scoutReach(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  avoidDanger = false,
  stop?: (cell: Cell, key: HexTileMapKey) => boolean
): Reachable {
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const rival = seenRivalTerritory(state, cache, unit.owner, oc);
  const own = oc.derived.blockers(unit.owner);
  const blocked = avoidDanger && danger.size > 0 ? new Set([...own, ...danger]) : own;
  const stepCost = (key: HexTileMapKey) =>
    1 + (danger.has(key) ? SCOUT_DANGER_COST : 0) + (rival.has(key) ? SCOUT_RIVAL_COST : 0);
  return cheapestFrom(ctx.terrain, unit.pos, terrain, blocked, stepCost, stop);
}

/** Unexplored map tiles within `radius` of `cell` (memoised for the tick: exploring happens later). */
function unexploredAround(ctx: SimContext, oc: OwnerCache, cell: Cell, radius: number): Cell[] {
  oc.unexplored ??= new Map();
  const memo = `${radius}:${getHexTileMapKey(cell)}`;
  let found = oc.unexplored.get(memo);
  if (!found) {
    // The disc in `hexesWithinRadius` order, read off the grid.
    const grid = gridOf(ctx.terrain);
    const bits = unexploredBits(ctx, oc);
    found = [];
    for (let dq = -radius; dq <= radius; dq++) {
      const lo = Math.max(-radius, -dq - radius);
      const hi = Math.min(radius, -dq + radius);
      for (let dr = lo; dr <= hi; dr++) {
        const at = tileAt(grid, cell.q + dq, cell.r + dr);
        if (at >= 0 && bits[at]) found.push({ q: cell.q + dq, r: cell.r + dr });
      }
    }
    oc.unexplored.set(memo, found);
  }
  return found;
}

/** Hex distance from `cell` to the nearest enemy fighter `owner` sees; infinite when none. */
function nearestThreat(
  state: SimState,
  ctx: SimContext,
  owner: PlayerId,
  oc: OwnerCache,
  cell: Cell
): number {
  let nearest = Number.POSITIVE_INFINITY;
  for (const t of threatsOf(state, ctx, owner, oc)) {
    nearest = Math.min(nearest, axialHexDistance(t, cell));
  }
  return nearest;
}

/**
 * Runs a threatened scout to safety: its nearest shelter tile (own garrison
 * territory, where it can't be hit) or a tile out of danger at least
 * SCOUT_FLEE_DISTANCE from every enemy fighter it sees, preferring shelter.
 */
function fleeScout(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[]
): void {
  const auto = unit.auto;
  if (!auto) return;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const { shelters } = ownTerritory(state, ctx, cache, unit.owner, oc);
  const safe = (cell: Cell) => {
    const key = getHexTileMapKey(cell);
    if (shelters.has(key)) return true;
    return (
      !danger.has(key) && nearestThreat(state, ctx, unit.owner, oc, cell) >= SCOUT_FLEE_DISTANCE
    );
  };
  auto.phase = 'flee';
  delete auto.retryTick;
  delete auto.blocked;
  // The dare timer starts again once it's back to waiting after the flee.
  delete auto.waitSince;
  if (isMoving(unit) && auto.target && safe(auto.target)) return;
  // Only the first SCOUT_MAX_CANDIDATES tiles are weighed.
  let reached = 0;
  const reach = scoutReach(
    state,
    ctx,
    cache,
    unit,
    terrain,
    false,
    () => reached++ >= SCOUT_MAX_CANDIDATES - 1
  );
  let best: Cell | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const cell of reach.order.slice(0, SCOUT_MAX_CANDIDATES)) {
    if (!safe(cell)) continue;
    const key = getHexTileMapKey(cell);
    const score = (reach.dist.get(key) ?? 0) + (shelters.has(key) ? 0 : SCOUT_SHELTER_BIAS);
    if (score < bestScore) {
      bestScore = score;
      best = cell;
    }
  }
  if (!best) {
    // Cornered: nowhere safe within reach, keep what it's doing.
    delete auto.target;
    return;
  }
  redirect(state, unit, pathTo(reach, best));
  auto.target = { q: best.q, r: best.r };
}

/**
 * Sends a scout with nothing left to explore home, out of harm's way: into
 * its home city when that takes scouts (rules `accepts`), else onto the
 * nearest free open tile of its own territory. No target is set, so the
 * walk isn't mistaken for exploring (stepScout leaves such a path to walk).
 * One already inside, on its way, or standing in its own territory stays.
 */
function scoutHome(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[]
): void {
  if (unit.inside || isMoving(unit)) return;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const { territory } = ownTerritory(state, ctx, cache, unit.owner, oc);
  const home = homeCity(state, ctx, unit);
  const occ = cache.derived.occupancy();
  // Round danger: a way home through it would only end in a flee and the
  // same plan again. With no safe way, it stays put (it's out of danger:
  // stepScout flees first), looking again on its next retry.
  const reach = scoutReach(state, ctx, cache, unit, terrain, true);
  let dest: Cell | undefined;
  if (
    home &&
    reach.dist.has(getHexTileMapKey(home.pos)) &&
    stopKind(ctx.rules, occ, unit, home.pos, true) === 'inside'
  ) {
    dest = home.pos;
  } else if (!territory.has(getHexTileMapKey(unit.pos))) {
    // Cost order: the first free own tile found is the cheapest to reach.
    dest = reach.order.find(
      (cell) =>
        territory.has(getHexTileMapKey(cell)) &&
        stopKind(ctx.rules, occ, unit, cell, true) === 'open'
    );
  }
  if (!dest) return;
  redirect(state, unit, pathTo(reach, dest));
  forget(occ, unit);
  addHeading(occ, unit, dest);
}

/**
 * The frontier a scout with no safe one to explore waits for: the cheapest
 * explored tile next to unexplored map it could reach if danger and rival
 * units (both passing) didn't stop it, only terrain and the rival buildings
 * its owner sees; steps into danger cost extra, so it's the one least deep
 * in danger. Null when there's none: nothing is left to explore from here.
 */
function guardedFrontier(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[]
): Cell | null {
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const vision = oc.derived.vision(unit.owner);
  const buildings = new Set<HexTileMapKey>();
  for (const b of Object.values(state.buildings)) {
    if (b.owner === unit.owner) continue;
    for (const cell of buildingCells(b)) {
      const key = getHexTileMapKey(cell);
      if (vision.has(key)) buildings.add(key);
    }
  }
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const reach = cheapestFrom(
    ctx.terrain,
    unit.pos,
    terrain,
    buildings,
    (key) => 1 + (danger.has(key) ? SCOUT_DANGER_COST : 0),
    (cell) => isFrontier(ctx, oc, cell)
  );
  const last = reach.order[reach.order.length - 1];
  return last && isFrontier(ctx, oc, last) ? last : null;
}

/**
 * Sends a waiting scout to the safe tile nearest (by hex distance) to the
 * `frontier` it waits for, out of danger and where it can stop (open ground,
 * or its own tile); ties go to the cheaper walk. No target is set, as for
 * `scoutHome`. One already walking there, or standing as close, stays.
 */
function waitNear(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  frontier: Cell
): void {
  if (isMoving(unit)) return;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const occ = cache.derived.occupancy();
  const here = getHexTileMapKey(unit.pos);
  let best: Cell | null = danger.has(here) ? null : unit.pos;
  let bestDist = best ? axialHexDistance(best, frontier) : Number.POSITIVE_INFINITY;
  const reach = scoutReach(state, ctx, cache, unit, terrain, true);
  for (const cell of reach.order) {
    const key = getHexTileMapKey(cell);
    if (key === here || danger.has(key)) continue;
    const d = axialHexDistance(cell, frontier);
    if (d >= bestDist) continue;
    if (stopKind(ctx.rules, occ, unit, cell, true) !== 'open') continue;
    best = cell;
    bestDist = d;
  }
  if (!best || getHexTileMapKey(best) === here) return;
  redirect(state, unit, pathTo(reach, best));
  forget(occ, unit);
  addHeading(occ, unit, best);
}

/**
 * After waiting SCOUT_DARE_TICKS, a scout takes the cheapest way to a
 * frontier through danger (`auto.dare`: it doesn't flee or re-plan for
 * danger on the way). False when even that finds none (rival units block it).
 */
function dareFrontier(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[]
): boolean {
  const auto = unit.auto;
  if (!auto) return false;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const reach = scoutReach(state, ctx, cache, unit, terrain, false, (c) => isFrontier(ctx, oc, c));
  const last = reach.order[reach.order.length - 1];
  if (!last || !isFrontier(ctx, oc, last) || getHexTileMapKey(last) === getHexTileMapKey(unit.pos))
    return false;
  redirect(state, unit, pathTo(reach, last));
  auto.target = { q: last.q, r: last.r };
  auto.dare = true;
  delete auto.retryTick;
  delete auto.blocked;
  delete auto.waitSince;
  return true;
}

/** Whether `cell` is near a frontier `unit` found dangerous (`auto.avoid`) and out of its owner's sight. */
function avoided(oc: OwnerCache, unit: Unit, cell: Cell, key: HexTileMapKey): boolean {
  const avoid = unit.auto?.avoid;
  if (!avoid?.some((a) => axialHexDistance(a, cell) <= SCOUT_AVOID_RADIUS)) return false;
  return !oc.derived.vision(unit.owner).has(key);
}

/** Remembers `cell` as a dangerous frontier for `unit` (the oldest forgotten past SCOUT_MAX_AVOID). */
function addAvoid(unit: Unit, cell: Cell): void {
  const auto = unit.auto;
  if (!auto) return;
  const list = (auto.avoid ?? []).filter((a) => a.q !== cell.q || a.r !== cell.r);
  list.push({ q: cell.q, r: cell.r });
  auto.avoid = list.slice(-SCOUT_MAX_AVOID);
}

/**
 * Patrol spots: the map tiles whose q and r are both multiples of `spacing`
 * (a triangular lattice, so every tile is within about two thirds of
 * `spacing` of one). Memoised per terrain and spacing.
 */
const patrolLattices = new WeakMap<SimContext['terrain'], Map<number, Cell[]>>();

function patrolLattice(ctx: SimContext, spacing: number): Cell[] {
  let bySpacing = patrolLattices.get(ctx.terrain);
  if (!bySpacing) {
    bySpacing = new Map();
    patrolLattices.set(ctx.terrain, bySpacing);
  }
  let cells = bySpacing.get(spacing);
  if (!cells) {
    const on = (n: number) => ((n % spacing) + spacing) % spacing === 0;
    cells = [];
    for (const tile of ctx.terrain.values()) {
      if (on(tile.coordinates.q) && on(tile.coordinates.r)) cells.push(tile.coordinates);
    }
    cells.sort((a, b) => a.q - b.q || a.r - b.r);
    bySpacing.set(spacing, cells);
  }
  return cells;
}

/**
 * Patrol, for a scout with the map explored: every SCOUT_PATROL_TICKS it
 * picks the patrol spot (`patrolLattice`) its owner has had in sight
 * longest ago (`PlayerState.looked`; never is longest), outside its own
 * territory, explored, out of the danger it sees and reachable round it,
 * nearer first on ties, and walks there to look (`phase` 'patrol'). Spots
 * near another own scout's target (`SCOUT_PATROL_SPREAD`) come last, so
 * scouts picking on the same tick split up rather than clump. It
 * waits there until the next pick, so it keeps an eye out in the field
 * rather than in the city. Spots in sight are marked looked at as it
 * picks, so it's all from its owner's own knowledge. False when there's
 * no spot to go to (it goes home).
 */
function patrol(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  radius: number
): boolean {
  const auto = unit.auto;
  const player = state.players[unit.owner];
  if (!auto || !player) return false;
  if (auto.patrolSince !== undefined && state.tick - auto.patrolSince < SCOUT_PATROL_TICKS) {
    auto.retryTick = auto.patrolSince + SCOUT_PATROL_TICKS;
    return true;
  }
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const spots = patrolLattice(ctx, radius);
  const vision = oc.derived.vision(unit.owner);
  const looked = player.looked ?? {};
  for (const cell of spots) {
    const key = getHexTileMapKey(cell);
    if (vision.has(key)) looked[key] = state.tick;
  }
  if (Object.keys(looked).length > 0) player.looked = looked;
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const { territory } = ownTerritory(state, ctx, cache, unit.owner, oc);
  const reach = scoutReach(state, ctx, cache, unit, terrain, true);
  const here = getHexTileMapKey(unit.pos);
  const targets = scoutTargets(state, ctx, unit.owner, oc);
  const others = [...targets].filter(([id]) => id !== unit.id).map(([, t]) => t);
  const spread = SCOUT_PATROL_SPREAD * radius;
  let best: Cell | null = null;
  let bestClear = false;
  let bestSeen = Number.POSITIVE_INFINITY;
  let bestCost = Number.POSITIVE_INFINITY;
  for (const cell of spots) {
    const key = getHexTileMapKey(cell);
    if (key === here || territory.has(key) || danger.has(key) || !oc.explored(key)) continue;
    const cost = reach.dist.get(key);
    if (cost === undefined) continue;
    const clear = !others.some((t) => axialHexDistance(t, cell) < spread);
    const seen = looked[key] ?? -1;
    if (clear !== bestClear) {
      if (!clear) continue;
    } else if (seen > bestSeen || (seen === bestSeen && cost >= bestCost)) continue;
    best = cell;
    bestClear = clear;
    bestSeen = seen;
    bestCost = cost;
  }
  if (!best) return false;
  redirect(state, unit, pathTo(reach, best));
  auto.phase = 'patrol';
  auto.target = { q: best.q, r: best.r };
  targets.set(unit.id, auto.target);
  auto.patrolSince = state.tick;
  delete auto.retryTick;
  return true;
}

/**
 * Explore automation. A scout heads for the frontier tile (explored, next to
 * unexplored map) with the most information for the walk: the unexplored
 * tiles within its vision of the tile, counting ones another scout is
 * already heading for at SCOUT_COVERED_WEIGHT, over (walking cost +
 * SCOUT_DISTANCE_OFFSET vision radii), times a spread factor that falls as
 * the tile gets near another scout's target, or another scout that's nearer
 * to it. So it goes for big unexplored pockets, not the nearest sliver, and
 * scouts split up. Walking cost counts danger and seen rival territory
 * (`scoutReach`). Within SCOUT_DANGER_RADIUS of a visible enemy fighter (or
 * in range of a rival building that fires), outside its own shelter, a scout
 * drops its target and flees (`fleeScout`) until none is within SAFE_RADIUS,
 * then explores again. Exploring paths never cross danger (`scoutReach`
 * `avoidDanger`). A chosen target is kept until it's reached, stops
 * bordering unexplored map, or turns dangerous (in danger, or no safe way
 * to it left: a path running into danger is re-planned to the same
 * target first); a dangerous one is remembered (`auto.avoid`), and
 * frontiers near it out of sight count as dangerous, so the scout doesn't
 * swing to the side of a guarded pocket that only looks safe. When the
 * frontier left is only reachable through danger, in it, or past rival
 * units in the way (`guardedFrontier`), the scout is `blocked`: it waits at
 * the safe tile nearest that frontier (`waitNear`; the nearest one it
 * avoids, so the spot doesn't wander), looking again every
 * SCOUT_WAIT_RETRY_TICKS, and after SCOUT_DARE_TICKS without exploring
 * anything (`auto.waitSince`, kept across targets picked between waits)
 * dares the way (`dareFrontier`). With nothing left to explore at all it
 * patrols (`patrol`), else goes home and waits there (`scoutHome`),
 * looking again every SCOUT_IDLE_RETRY_TICKS.
 */
function stepScout(state: SimState, ctx: SimContext, cache: TickCache, unit: Unit): void {
  const auto = unit.auto;
  const def = ctx.rules.units[unit.type];
  if (!auto || !def) return;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const danger = scoutDanger(state, ctx, unit.owner, oc);
  const posKey = getHexTileMapKey(unit.pos);
  const radius = Math.max(1, def.visionRadius);
  // A dare ends where its path does.
  if (auto.dare && !isMoving(unit)) delete auto.dare;
  // The dare timer runs until the scout explores something (its target
  // reached, or explored on the way): a new target picked between waits
  // doesn't reset it, or a pocket whose guards come into sight one side at a
  // time (the other side looking safe again) would keep it walking back and
  // forth for good.
  if (
    auto.phase === undefined &&
    auto.target &&
    (!isMoving(unit) || !isFrontier(ctx, oc, auto.target))
  ) {
    delete auto.waitSince;
    delete auto.avoid;
  }
  // A dangerous frontier explored since (by anyone) is forgotten.
  if (auto.avoid) {
    auto.avoid = auto.avoid.filter((a) => isFrontier(ctx, oc, a));
    if (auto.avoid.length === 0) delete auto.avoid;
  }

  // Threatened: run, and keep running until clear (not while daring).
  if (danger.size > 0 && !unit.inside && !auto.dare) {
    const sheltered = ownTerritory(state, ctx, cache, unit.owner, oc).shelters.has(posKey);
    const threatened =
      danger.has(posKey) ||
      (auto.phase === 'flee' && nearestThreat(state, ctx, unit.owner, oc, unit.pos) <= SAFE_RADIUS);
    if (threatened && !sheltered) {
      fleeScout(state, ctx, cache, unit, def.terrain);
      return;
    }
  }
  if (auto.phase === 'flee') {
    // Clear again: explore afresh.
    delete auto.phase;
    delete auto.target;
    redirect(state, unit, [unit.pos]);
  }

  const crossesDanger = () => !!unit.path?.some((c) => danger.has(getHexTileMapKey(c)));
  // On patrol: keep walking while the way is clear; danger on it ends the
  // walk, and that spot counts as looked at, so the next pick goes elsewhere.
  if (auto.phase === 'patrol' && isMoving(unit) && auto.target) {
    if (!crossesDanger()) return;
    const player = state.players[unit.owner];
    if (player) player.looked = { ...player.looked, [getHexTileMapKey(auto.target)]: state.tick };
    redirect(state, unit, [unit.pos]);
    delete auto.target;
    delete auto.patrolSince;
  }
  // Keep going while the target is still worth reaching (committed: it isn't
  // scored again on the way). A target that stopped being a frontier
  // (explored on the way) is only replaced on this scout's own tick of every
  // RETRY_TICKS, so scouts that step together don't all search the island
  // on the same tick.
  const ownTick = (state.tick + mintOrder(unit.id)) % RETRY_TICKS === 0;
  if (
    isMoving(unit) &&
    auto.target &&
    auto.phase !== 'patrol' &&
    (!ownTick || isFrontier(ctx, oc, auto.target))
  ) {
    if (auto.dare) return;
    const targetKey = getHexTileMapKey(auto.target);
    if (!danger.has(targetKey)) {
      if (!crossesDanger()) return;
      // Danger came into view on the way but not at the target: round it.
      const reach = scoutReach(
        state,
        ctx,
        cache,
        unit,
        def.terrain,
        true,
        (_c, k) => k === targetKey
      );
      if (reach.dist.has(targetKey)) {
        redirect(state, unit, pathTo(reach, auto.target));
        return;
      }
    }
    // The target turned dangerous (guards out of sight when it was picked
    // came into view): remember it and look again. That's waiting too, so
    // the dare timer runs.
    addAvoid(unit, auto.target);
    auto.waitSince ??= state.tick;
    redirect(state, unit, [unit.pos]);
    delete auto.target;
    delete auto.retryTick;
  }
  // A waiting scout's walk (`waitNear`) that now runs into danger (more of
  // the guard came into view) is dropped and planned again now.
  if (
    isMoving(unit) &&
    !auto.target &&
    auto.blocked &&
    unit.path?.some((c) => danger.has(getHexTileMapKey(c)))
  ) {
    redirect(state, unit, [unit.pos]);
    delete auto.retryTick;
  }
  // Waiting to retry: a path it didn't set for exploring (settle stepping it
  // off a taken tile) is left to walk.
  if (
    (!isMoving(unit) || !auto.target) &&
    auto.retryTick !== undefined &&
    state.tick < auto.retryTick
  ) {
    return;
  }
  if (
    auto.waitSince !== undefined &&
    state.tick - auto.waitSince >= SCOUT_DARE_TICKS &&
    dareFrontier(state, ctx, cache, unit, def.terrain)
  ) {
    if (auto.target) scoutTargets(state, ctx, unit.owner, oc).set(unit.id, auto.target);
    return;
  }
  // A waiting scout keeps walking to its wait spot rather than trying a
  // side that looks safe from here and being turned back again, back and
  // forth across the pocket; it looks again once there.
  if (auto.blocked && !auto.target && isMoving(unit)) {
    auto.retryTick = retryAt(state, unit, SCOUT_WAIT_RETRY_TICKS);
    return;
  }

  const targets = scoutTargets(state, ctx, unit.owner, oc);
  targets.delete(unit.id);
  const others = [...targets.values()];
  const positions = [...scoutPositions(state, ctx, unit.owner, oc)]
    .filter(([id]) => id !== unit.id)
    .map(([, pos]) => pos);
  const cap = SCOUT_SEPARATION_CAP * radius;
  const offset = SCOUT_DISTANCE_OFFSET * radius;
  // Only the first SCOUT_MAX_CANDIDATES frontier tiles are weighed, so the
  // search stops there.
  let found = 0;
  const reach = scoutReach(
    state,
    ctx,
    cache,
    unit,
    def.terrain,
    true,
    (cell, key) => !danger.has(key) && isFrontier(ctx, oc, cell) && ++found >= SCOUT_MAX_CANDIDATES
  );
  let choice: Cell | null = null;
  let bestScore = 0;
  let candidates = 0;
  // Cost order: on equal scores the cheaper frontier wins.
  for (const cell of reach.order) {
    const key = getHexTileMapKey(cell);
    if (danger.has(key) || !isFrontier(ctx, oc, cell)) continue;
    if (++candidates > SCOUT_MAX_CANDIDATES) break;
    if (avoided(oc, unit, cell, key)) continue;
    let gain = 0;
    for (const u of unexploredAround(ctx, oc, cell, radius)) {
      gain += others.some((t) => axialHexDistance(t, u) <= radius) ? SCOUT_COVERED_WEIGHT : 1;
    }
    let separation = cap;
    for (const t of others) separation = Math.min(separation, axialHexDistance(t, cell));
    const mine = axialHexDistance(unit.pos, cell);
    for (const p of positions) {
      const d = axialHexDistance(p, cell);
      if (d < mine) separation = Math.min(separation, d);
    }
    const spread = SCOUT_SPREAD_FLOOR + ((1 - SCOUT_SPREAD_FLOOR) * separation) / cap;
    const score = (gain * spread) / ((reach.dist.get(key) ?? 0) + offset);
    if (score > bestScore) {
      bestScore = score;
      choice = cell;
    }
  }

  if (!choice) {
    // Nothing left to explore from here, or only through or in danger (or
    // past rival units): then it's `blocked` (waiting near it for the way to
    // clear), not done. Only an exploring path is dropped: one settle gave
    // it (off a taken tile) stays, or settle would hand it back every tick
    // and it would never leave.
    // A patrol walk that ended stays put (no path to drop).
    if (auto.target && auto.phase !== 'patrol') {
      redirect(state, unit, [unit.pos]);
    }
    delete auto.target;
    delete auto.dare;
    // Waits by the nearest frontier it avoids, so the spot doesn't wander
    // with whichever guarded frontier is cheapest from where it stands.
    let frontier: Cell | null = null;
    for (const a of auto.avoid ?? []) {
      if (!frontier || axialHexDistance(a, unit.pos) < axialHexDistance(frontier, unit.pos))
        frontier = a;
    }
    frontier ??= guardedFrontier(state, ctx, cache, unit, def.terrain);
    if (frontier) {
      addAvoid(unit, frontier);
      if (auto.phase === 'patrol') delete auto.phase;
      delete auto.patrolSince;
      auto.blocked = true;
      auto.waitSince ??= state.tick;
      auto.retryTick = retryAt(state, unit, SCOUT_WAIT_RETRY_TICKS);
      waitNear(state, ctx, cache, unit, def.terrain, frontier);
    } else {
      delete auto.blocked;
      delete auto.waitSince;
      if (!patrol(state, ctx, cache, unit, def.terrain, radius)) {
        if (auto.phase === 'patrol') delete auto.phase;
        delete auto.patrolSince;
        auto.retryTick = retryAt(state, unit, SCOUT_IDLE_RETRY_TICKS);
        scoutHome(state, ctx, cache, unit, def.terrain);
      }
    }
    return;
  }
  redirect(state, unit, pathTo(reach, choice));
  if (auto.phase === 'patrol') delete auto.phase;
  delete auto.patrolSince;
  auto.target = { q: choice.q, r: choice.r };
  delete auto.retryTick;
  delete auto.blocked;
  delete auto.dare;
  targets.set(unit.id, auto.target);
}

export { MAX_LOOKED, SCOUT_MAX_AVOID, stepScout };
